if (!customElements.get('offer-banner-showcase')) {
  class OfferBannerShowcase extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.stage = this.querySelector('[data-offer-stage]');
      this.slides = Array.from(this.querySelectorAll('[data-offer-slide]'));
      if (!this.stage || this.slides.length === 0) return;

      this.initialized = true;
      this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.previousButton = this.querySelector('[data-offer-previous]');
      this.nextButton = this.querySelector('[data-offer-next]');
      this.count = this.querySelector('[data-offer-count]');
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.dataset.designMode !== 'true' && this.slides.length > 1;
      this.autoplayDelay = Math.max(3000, Number.parseInt(this.dataset.speed, 10) || 3000);
      this.desktopQuery = window.matchMedia('(min-width: 990px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.direction = 1;
      this.isSettling = false;
      this.hoverPaused = false;
      this.focusPaused = false;
      this.inViewport = true;
      this.mobileFrame = null;
      this.motionAnimations = [];

      this.bindEvents();
      this.observeVisibility();
      this.syncMode();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.visibilityObserver?.disconnect();
      this.clearAutoplay();
      this.clearSettle();
      this.cancelMotionAnimations();
      if (this.mobileFrame) window.cancelAnimationFrame(this.mobileFrame);
      this.initialized = false;
    }

    bindEvents() {
      this.previousButton?.addEventListener('click', () => this.goTo(this.currentIndex - 1, 'control'), { signal: this.signal });
      this.nextButton?.addEventListener('click', () => this.goTo(this.currentIndex + 1, 'control'), { signal: this.signal });

      this.slides.forEach((slide, index) => {
        slide.addEventListener(
          'click',
          (event) => {
            if (!this.desktopQuery.matches || index === this.currentIndex) return;
            event.preventDefault();
            this.goTo(index, 'side-card');
          },
          { signal: this.signal }
        );
      });

      this.addEventListener('keydown', (event) => this.onKeydown(event), { signal: this.signal });
      this.addEventListener(
        'mouseenter',
        () => {
          this.hoverPaused = true;
          this.clearAutoplay();
        },
        { signal: this.signal }
      );
      this.addEventListener(
        'mouseleave',
        () => {
          this.hoverPaused = false;
          this.scheduleAutoplay();
        },
        { signal: this.signal }
      );
      this.addEventListener(
        'focusin',
        () => {
          this.focusPaused = true;
          this.clearAutoplay();
        },
        { signal: this.signal }
      );
      this.addEventListener(
        'focusout',
        (event) => {
          if (event.relatedTarget && this.contains(event.relatedTarget)) return;
          this.focusPaused = false;
          this.scheduleAutoplay();
        },
        { signal: this.signal }
      );

      this.stage.addEventListener('scroll', () => this.onMobileScroll(), { passive: true, signal: this.signal });
      document.addEventListener('visibilitychange', () => (document.hidden ? this.clearAutoplay() : this.scheduleAutoplay()), {
        signal: this.signal,
      });
      document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal: this.signal });
      this.desktopQuery.addEventListener('change', () => this.syncMode(), { signal: this.signal });
      this.reducedMotion.addEventListener('change', () => this.syncMode(), { signal: this.signal });
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;

      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          this.inViewport = entries[0]?.isIntersecting ?? true;
          if (this.inViewport) this.scheduleAutoplay();
          else this.clearAutoplay();
        },
        { threshold: 0.2 }
      );
      this.visibilityObserver.observe(this);
    }

    syncMode() {
      this.clearAutoplay();
      this.clearSettle();
      this.cancelMotionAnimations();
      this.classList.remove('is-rail-transitioning');

      if (this.desktopQuery.matches) {
        this.dataset.mode = 'desktop';
        this.syncDesktopSlides();
        this.promoteActive();
      } else {
        this.dataset.mode = 'mobile';
        this.slides.forEach((slide) => {
          slide.setAttribute('aria-hidden', 'false');
          this.setSlideInteractive(slide, true);
        });
        this.syncMobileActive();
      }
    }

    onKeydown(event) {
      if (!this.desktopQuery.matches || event.altKey || event.ctrlKey || event.metaKey) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.currentIndex - 1, 'keyboard');
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.currentIndex + 1, 'keyboard');
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0, 'keyboard');
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.slides.length - 1, 'keyboard');
      }
    }

    goTo(index, source = 'control') {
      if (!this.desktopQuery.matches || this.slides.length < 2) return;

      const target = Math.max(0, Math.min(index, this.slides.length - 1));
      if (target === this.currentIndex) {
        this.scheduleAutoplay();
        return;
      }

      const shouldAnimate = !this.reducedMotion.matches && typeof this.slides[0]?.animate === 'function';
      const snapshots = shouldAnimate ? this.captureMotionState() : null;

      this.clearAutoplay();
      this.clearSettle();
      this.cancelMotionAnimations();
      this.isSettling = true;
      if (shouldAnimate) this.classList.add('is-rail-transitioning');
      this.slides.forEach((slide) => slide.classList.remove('is-active'));
      this.currentIndex = target;
      this.syncDesktopSlides();

      if (shouldAnimate) this.animateRailFrom(snapshots);

      const settleDuration = shouldAnimate ? 680 : 0;
      this.settleTimer = window.setTimeout(() => {
        if (shouldAnimate) this.finishRailTransition();
        else this.promoteActive();
      }, settleDuration);

      if (source !== 'autoplay' && this.count) {
        this.count.setAttribute('aria-label', `Offer ${this.currentIndex + 1} of ${this.slides.length}`);
      }
    }

    syncDesktopSlides() {
      this.slides.forEach((slide, index) => {
        const difference = index - this.currentIndex;
        let slot = 'hidden';
        if (difference === 0) slot = 'center';
        else if (difference === -1) slot = 'left';
        else if (difference === 1) slot = 'right';
        else if (Math.abs(difference) === 2) slot = 'far';

        slide.dataset.slot = slot;
        slide.dataset.side = difference < 0 ? 'left' : 'right';
        slide.setAttribute('aria-hidden', difference === 0 ? 'false' : 'true');
        this.setSlideInteractive(slide, difference === 0);
      });

      this.syncControls();
    }

    promoteActive() {
      this.clearSettle();
      this.isSettling = false;
      this.slides[this.currentIndex]?.classList.add('is-active');
      this.syncControls();
      this.scheduleAutoplay();
    }

    captureMotionState() {
      return this.slides.map((slide) => {
        const slideStyle = window.getComputedStyle(slide);
        const media = slide.querySelector('.offer-showcase__media');
        const mediaStyle = media ? window.getComputedStyle(media) : null;

        return {
          slide: {
            transform: slideStyle.transform,
            opacity: slideStyle.opacity,
            boxShadow: slideStyle.boxShadow,
          },
          media: mediaStyle
            ? {
                filter: mediaStyle.filter,
                transform: mediaStyle.transform,
              }
            : null,
        };
      });
    }

    animateRailFrom(snapshots) {
      const railTiming = {
        duration: 680,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'both',
      };
      const mediaTiming = {
        duration: 460,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'both',
      };

      this.slides.forEach((slide, index) => {
        const snapshot = snapshots[index];
        let targetSlide;
        if (index === this.currentIndex) {
          slide.classList.add('is-active');
          const activeStyle = window.getComputedStyle(slide);
          targetSlide = {
            transform: activeStyle.transform,
            opacity: activeStyle.opacity,
            boxShadow: activeStyle.boxShadow,
          };
          slide.classList.remove('is-active');
        } else {
          const targetStyle = window.getComputedStyle(slide);
          targetSlide = {
            transform: targetStyle.transform,
            opacity: targetStyle.opacity,
            boxShadow: targetStyle.boxShadow,
          };
        }

        this.motionAnimations.push(
          slide.animate([snapshot.slide, targetSlide], railTiming)
        );

        const media = slide.querySelector('.offer-showcase__media');
        if (!media || !snapshot.media) return;
        const targetMediaStyle = window.getComputedStyle(media);
        this.motionAnimations.push(
          media.animate(
            [
              snapshot.media,
              {
                filter: targetMediaStyle.filter,
                transform: targetMediaStyle.transform,
              },
            ],
            mediaTiming
          )
        );
      });
    }

    finishRailTransition() {
      const activeSlide = this.slides[this.currentIndex];
      const activeMedia = activeSlide?.querySelector('.offer-showcase__media');
      const mediaFrom = activeMedia
        ? {
            filter: window.getComputedStyle(activeMedia).filter,
            transform: window.getComputedStyle(activeMedia).transform,
          }
        : null;

      activeSlide?.classList.add('is-active');
      this.classList.remove('is-rail-transitioning');
      this.cancelMotionAnimations();

      if (activeMedia && mediaFrom) {
        const mediaTarget = window.getComputedStyle(activeMedia);
        this.motionAnimations.push(
          activeMedia.animate(
            [
              mediaFrom,
              {
                filter: mediaTarget.filter,
                transform: mediaTarget.transform,
              },
            ],
            {
              duration: 320,
              easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
            }
          )
        );
      }

      this.isSettling = false;
      this.syncControls();
      this.scheduleAutoplay();
    }

    cancelMotionAnimations() {
      this.motionAnimations?.forEach((animation) => animation.cancel());
      this.motionAnimations = [];
    }

    syncControls() {
      const total = String(this.slides.length).padStart(2, '0');
      if (this.count) this.count.textContent = `${String(this.currentIndex + 1).padStart(2, '0')} / ${total}`;
      this.style.setProperty('--offer-progress', String((this.currentIndex + 1) / this.slides.length));
      if (this.previousButton) this.previousButton.disabled = this.currentIndex === 0;
      if (this.nextButton) this.nextButton.disabled = this.currentIndex === this.slides.length - 1;
      this.stage.setAttribute('aria-live', this.autoplayEnabled && !this.reducedMotion.matches ? 'off' : 'polite');
    }

    setSlideInteractive(slide, interactive) {
      if (!slide.matches('a[href]')) return;
      slide.setAttribute('tabindex', interactive ? '0' : '-1');
    }

    scheduleAutoplay() {
      this.clearAutoplay();
      if (
        !this.desktopQuery.matches ||
        !this.autoplayEnabled ||
        this.reducedMotion.matches ||
        this.hoverPaused ||
        this.focusPaused ||
        !this.inViewport ||
        document.hidden ||
        this.isSettling
      ) {
        return;
      }

      this.autoplayTimer = window.setTimeout(() => {
        if (this.currentIndex >= this.slides.length - 1) this.direction = -1;
        else if (this.currentIndex <= 0) this.direction = 1;
        this.goTo(this.currentIndex + this.direction, 'autoplay');
      }, this.autoplayDelay);
    }

    clearAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    clearSettle() {
      if (!this.settleTimer) return;
      window.clearTimeout(this.settleTimer);
      this.settleTimer = null;
    }

    onMobileScroll() {
      if (this.desktopQuery.matches || this.mobileFrame) return;
      this.mobileFrame = window.requestAnimationFrame(() => {
        this.mobileFrame = null;
        this.syncMobileActive();
      });
    }

    syncMobileActive() {
      const stageCenter = this.stage.scrollLeft + this.stage.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        const distance = Math.abs(slideCenter - stageCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      this.currentIndex = closestIndex;
      this.slides.forEach((slide, index) => slide.classList.toggle('is-active', index === closestIndex));
    }

    onBlockSelect(event) {
      const selectedSlide = event.target.closest?.('[data-offer-slide]');
      if (!selectedSlide || !this.contains(selectedSlide)) return;

      this.hoverPaused = true;
      this.clearAutoplay();
      if (this.desktopQuery.matches) this.goTo(Number.parseInt(selectedSlide.dataset.index, 10), 'editor');
      else selectedSlide.scrollIntoView({ behavior: this.reducedMotion.matches ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
    }
  }

  customElements.define('offer-banner-showcase', OfferBannerShowcase);
}
